/**
 * Created by vladislav on 12.10.2026
 */

// one ad type (rewarded or interstitial) of an AdsPlugin: its cached ad, load and play state;
// the plugin keeps what both types share and the rules where they meet (AdsPlugin.canCache)
const AdUnit = function (plugin, name) {
    this.plugin = plugin;
    this.name = name;

    this.loaderState = AdsPlugin.LOADER_STATE.IDLE;
    this.videoState = AdsPlugin.VIDEO_STATE.IDLE;

    this.cached = undefined;
    this.cachedTime = undefined;
    this.cachedErrorTime = undefined;
    this.cachedErrorCode = undefined;

    this.loadGen = 0;
    this.pendingLoad = false;

    this.playCallback = undefined;
    this.startTime = undefined;
    this.finishTime = undefined;
};

AdUnit.prototype.setLoaderState = function (state) {
    console.log(this.plugin.pluginName, "setLoaderState", this.name, state);
    this.loaderState = state;

    this.plugin.bus.trigger(`${this.name}:refresh`);
};

AdUnit.prototype.setVideoState = function (state) {
    console.log(this.plugin.pluginName, "setVideoState", this.name, state);
    this.videoState = state;
    this.plugin.bus.trigger("changeVideoState", this.name, state);
};

AdUnit.prototype.isLoading = function () {
    return this.loaderState === AdsPlugin.LOADER_STATE.LOADING;
};

AdUnit.prototype.isCached = function () {
    return this.loaderState === AdsPlugin.LOADER_STATE.READY;
};

AdUnit.prototype.isRunning = function () {
    return this.videoState === AdsPlugin.VIDEO_STATE.PLAYING;
};

AdUnit.prototype.canCache = function () {
    if (this.plugin.cantLoadAfterPlay && (this.finishTime || 0) + AdsPlugin.LOAD_AFTER_PLAY_TIME > Date.now()) {
        return false;
    }

    if (this.pendingLoad) {
        return false;
    }

    if ((this.cachedErrorTime || 0) + AdsPlugin.getLoadErrorTimeout(this.cachedErrorCode) > Date.now()) {
        return false;
    }

    return true;
};

AdUnit.prototype.cacheAd = function (callback) {
    callback = callback || function () {};

    if (this.isLoading()) {
        this.plugin.bus.once(`${this.name}:cached`, callback);
        return;
    }

    if (!this.plugin.canCache(this.name)) {
        callback(connector.CODE_FAILED);
        return;
    }

    if (this.isCached() && !this.plugin.isAdExpired(this.name)) {
        callback(connector.CODE_SUCCEED);
        return;
    }

    this.plugin.cacheAdInner(this.name, callback);
};

AdUnit.prototype.load = function (callback) {
    this.setLoaderState(AdsPlugin.LOADER_STATE.LOADING);

    this.plugin.bus.trigger(`${this.name}:caching`, this.plugin.pluginName);

    const gen = ++this.loadGen;
    let notified = false;
    let settled = false;

    // waiters get an answer after TRY_LOADING_TIMEOUT; the load itself keeps going (pendingLoad blocks a second one)
    // and its late result is still accepted, until ABANDON_LOADING_TIMEOUT gives up on it
    const noticeTimeout = setTimeout(() => {
        notified = true;
        this.pendingLoad = true;
        this.setLoaderState(AdsPlugin.LOADER_STATE.IDLE);

        this.plugin.bus.trigger(`${this.name}:cached`, AdsPlugin.ERROR_LOADING_TIMEOUT, this.plugin.pluginName);

        callback(AdsPlugin.ERROR_LOADING_TIMEOUT);
    }, AdsPlugin.TRY_LOADING_TIMEOUT);

    const abandonTimeout = setTimeout(() => {
        this.loadGen++;
        this.pendingLoad = false;
        this.cachedErrorTime = Date.now();
        this.cachedErrorCode = AdsPlugin.ERROR_LOADING_ABANDONED;

        this.plugin._abandonAd(this.name);

        this.plugin.bus.trigger(`${this.name}:cached`, AdsPlugin.ERROR_LOADING_ABANDONED, this.plugin.pluginName);
    }, AdsPlugin.ABANDON_LOADING_TIMEOUT);

    this.plugin._cacheAd(this.name, (code, loadedAd) => {
        if (settled || gen !== this.loadGen) {
            return;
        }

        settled = true;
        clearTimeout(noticeTimeout);
        clearTimeout(abandonTimeout);
        this.pendingLoad = false;

        if (code === connector.CODE_SUCCEED) {
            this.cached = loadedAd;
            this.cachedTime = Date.now();
            this.cachedErrorTime = undefined;
            this.setLoaderState(AdsPlugin.LOADER_STATE.READY);
        } else {
            this.cachedErrorTime = Date.now();
            this.cachedErrorCode = code;
            this.setLoaderState(AdsPlugin.LOADER_STATE.IDLE);
        }

        this.plugin.bus.trigger(`${this.name}:cached`, code, this.plugin.pluginName);

        if (!notified) {
            notified = true;
            callback(code);
        }
    });
};

AdUnit.prototype.consumeCachedAd = function () {
    const cachedAd = this.cached;

    this.cached = undefined;
    this.cachedTime = undefined;
    this.cachedErrorTime = undefined;

    return cachedAd;
};

AdUnit.prototype.whenCached = function (callback) {
    if (this.isCached()) {
        callback(connector.CODE_SUCCEED);
    } else if (this.isLoading() || this.pendingLoad) {
        this.plugin.bus.once(`${this.name}:cached`, callback);
    } else {
        this.plugin.cacheAdInner(this.name, callback);
    }
};

AdUnit.prototype.playAd = function (callback) {
    callback = callback || function () {};

    const plugin = this.plugin;
    const name = this.name;

    const expired = this.cachedTime + plugin.expirationPeriod <= Date.now();
    const cachedAd = this.consumeCachedAd();

    this.startTime = Date.now();

    this.setLoaderState(AdsPlugin.LOADER_STATE.IDLE);
    this.setVideoState(AdsPlugin.VIDEO_STATE.PLAYING);

    plugin.bus.trigger(`${name}:start`, plugin.pluginName);
    if (expired) {
        plugin.bus.trigger(`${name}:start:expired`, plugin.pluginName);
    }

    connector.metrics.logEvent(`${connector.EVENTS.ADS.START + name}_${plugin.pluginName}`);

    this.playCallback = connector.utils.once((code, raw) => {
        this.playCallback = undefined;
        this.finishTime = Date.now();

        this.setVideoState(AdsPlugin.VIDEO_STATE.IDLE);

        const success = code === connector.CODE_SUCCEED;

        plugin.bus.trigger(`${name}:close`, success, plugin.pluginName);
        if (name === AdsPlugin.REWARDED && success) {
            plugin.bus.trigger("rewarded:reward", plugin.pluginName);
        }

        if (expired) {
            plugin.bus.trigger(`${name}:close:expired`, plugin.pluginName);
        }

        callback(code, { raw });
    });

    if (plugin._testNoCallback) {
        delete plugin._testNoCallback;

        return;
    }

    const onPlayResult = connector.utils.once((code, raw) => {
        if (!plugin.customImpressionEvent
            && [connector.CODE_SUCCEED, ERRORS.SHOW_REWARDED.CLOSED_BY_USER, ERRORS.SHOW_INTERSTITIAL.CLOSED_BY_USER].includes(code)) {
            plugin.onImpressionEvent(name);
        }

        this.finishAd(code, raw);
    });

    plugin._playAd(name, cachedAd, (code, raw) => {
        if ([ERRORS.SHOW_REWARDED.AD_EXPIRED,
            ERRORS.SHOW_REWARDED.UNKNOWN,
            ERRORS.SHOW_REWARDED.NO_ADS,
            ERRORS.SHOW_INTERSTITIAL.UNKNOWN,
            ERRORS.SHOW_INTERSTITIAL.NO_ADS
        ].includes(code) && !plugin.isThrottled(name)) {
            const errorShortName = AdUnit.shortCode(code);

            console.log("[connector] Ad error, trying again", code);
            this.whenCached((code) => {
                console.log("[connector] Ad cached after error result:", code === connector.CODE_SUCCEED);

                const cacheErrorShortName = (`${code}`).replace("ads_loading_", "");

                plugin.bus.trigger("stream:logs", `ad_retry_c_${errorShortName}__${cacheErrorShortName}`);

                if (code === connector.CODE_SUCCEED) {
                    const cachedAd = this.consumeCachedAd();

                    this.setLoaderState(AdsPlugin.LOADER_STATE.IDLE);
                    this.setVideoState(AdsPlugin.VIDEO_STATE.PLAYING);

                    plugin._playAd(name, cachedAd, (code, raw) => {
                        plugin.bus.trigger("stream:logs", `ad_retry_p_${errorShortName}__${AdUnit.shortCode(code)}`);

                        onPlayResult(code, raw);
                    });
                } else if (name === AdsPlugin.REWARDED) {
                    onPlayResult(ERRORS.SHOW_REWARDED.NO_ADS, raw);
                } else {
                    onPlayResult(ERRORS.SHOW_INTERSTITIAL.NO_ADS, raw);
                }
            });
        } else {
            onPlayResult(code, raw);
        }
    });
};

AdUnit.prototype.finishAd = function (code, raw) {
    if (this.playCallback) {
        this.playCallback(code, raw);
    }
};

// short form of an error code for the ad_retry_* log names
AdUnit.shortCode = function (code) {
    return `${code}`.replace("error", "e")
        .replace("rewarded", "r")
        .replace("interstitial", "i");
};
